// src/pages/LoginPage.jsx
// Login page with Google, Microsoft OAuth, and email/password options

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const API_BASE = process.env.REACT_APP_API_BASE || 'https://api.invenire.app';

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, loading, loginWithGoogle, loginWithMicrosoft, login } = useAuth();

  // Form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [pendingMessage, setPendingMessage] = useState('');

  // Get redirect destination from query params or state
  const params = new URLSearchParams(location.search);
  const redirectTo = params.get('redirect') || location.state?.from?.pathname || '/admin';
  
  // Check if coming back from pending state
  const isPending = params.get('pending') === 'true';
  const pendingEmail = params.get('email');

  // Show pending message if redirected here
  useEffect(() => {
    if (isPending) {
      setPendingMessage(
        `Your sign-in with ${pendingEmail || 'your account'} has been recorded. ` +
        `The administrator has been notified. You will receive an email once you are approved for access.`
      );
    }
  }, [isPending, pendingEmail]);

  // Redirect if already authenticated
  useEffect(() => {
    if (!loading && isAuthenticated) {
      navigate(redirectTo, { replace: true });
    }
  }, [isAuthenticated, loading, navigate, redirectTo]);

  // Get tenant key from current URL for pending notification
  const getTenantKey = () => {
    const hostname = window.location.hostname;
    if (hostname.includes('.invenire.app') && !hostname.includes('admin.invenire.app')) {
      return hostname.split('.')[0];
    }
    return null;
  };

  const handleGoogleLogin = () => {
    loginWithGoogle(window.location.origin);
  };

  const handleMicrosoftLogin = () => {
    loginWithMicrosoft(window.location.origin);
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      const response = await fetch(`${API_BASE}/auth/login/email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          password,
          tenant_key: getTenantKey()
        })
      });

      const data = await response.json();

      if (data.pending) {
        // User is pending approval
        setPendingMessage(data.message);
        setShowEmailForm(false);
      } else if (data.access_token) {
        // Login successful
        login(data.access_token, data.refresh_token);
        navigate(redirectTo, { replace: true });
      } else if (data.detail) {
        // Error from server
        setError(data.detail);
      } else {
        setError('An unexpected error occurred');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError('Failed to connect to server. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.loading}>Loading...</div>
        </div>
      </div>
    );
  }

  // Show pending message screen
  if (pendingMessage) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.logoSection}>
            <img 
              src="/invenire-logo-transparent.png" 
              alt="Invenire" 
              style={styles.logoImage}
            />
          </div>

          <div style={styles.pendingSection}>
            <div style={styles.pendingIcon}>⏳</div>
            <h2 style={styles.pendingTitle}>Pending Approval</h2>
            <p style={styles.pendingMessage}>{pendingMessage}</p>
          </div>

          <button
            onClick={() => {
              setPendingMessage('');
              navigate('/login', { replace: true });
            }}
            style={styles.backButton}
          >
            ← Back to Sign In
          </button>
        </div>
        <div style={styles.bgGradient} />
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        {/* Logo */}
        <div style={styles.logoSection}>
          <img 
            src="/invenire-logo-transparent.png" 
            alt="Invenire - AI hiring made human" 
            style={styles.logoImage}
          />
        </div>

        {/* Title */}
        <h2 style={styles.title}>Sign in to continue</h2>
        <p style={styles.subtitle}>
          Use your work account to access the dashboard
        </p>

        {/* Error message */}
        {error && (
          <div style={styles.error}>{error}</div>
        )}

        {!showEmailForm ? (
          <>
            {/* OAuth Buttons */}
            <div style={styles.buttonContainer}>
              <button 
                onClick={handleGoogleLogin}
                style={styles.oauthButton}
                onMouseOver={(e) => e.target.style.background = 'rgba(255,255,255,0.15)'}
                onMouseOut={(e) => e.target.style.background = 'rgba(255,255,255,0.1)'}
              >
                <svg style={styles.icon} viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                <span>Continue with Google</span>
              </button>

              <button 
                onClick={handleMicrosoftLogin}
                style={styles.oauthButton}
                onMouseOver={(e) => e.target.style.background = 'rgba(255,255,255,0.15)'}
                onMouseOut={(e) => e.target.style.background = 'rgba(255,255,255,0.1)'}
              >
                <svg style={styles.icon} viewBox="0 0 24 24">
                  <path fill="#F25022" d="M1 1h10v10H1z"/>
                  <path fill="#00A4EF" d="M1 13h10v10H1z"/>
                  <path fill="#7FBA00" d="M13 1h10v10H13z"/>
                  <path fill="#FFB900" d="M13 13h10v10H13z"/>
                </svg>
                <span>Continue with Microsoft</span>
              </button>
            </div>

            {/* Divider */}
            <div style={styles.divider}>
              <span style={styles.dividerLine}></span>
              <span style={styles.dividerText}>or</span>
              <span style={styles.dividerLine}></span>
            </div>

            {/* Email option */}
            <button
              onClick={() => setShowEmailForm(true)}
              style={styles.emailToggle}
            >
              Continue with Email
            </button>
          </>
        ) : (
          <>
            {/* Email/Password Form */}
            <form onSubmit={handleEmailLogin} style={styles.form}>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  style={styles.input}
                  required
                  autoFocus
                />
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}>Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  style={styles.input}
                  required
                />
              </div>

              <button
                type="submit"
                style={styles.submitButton}
                disabled={submitting}
              >
                {submitting ? 'Signing in...' : 'Sign In'}
              </button>
            </form>

            <button
              onClick={() => {
                setShowEmailForm(false);
                setError('');
              }}
              style={styles.backLink}
            >
              ← Back to other options
            </button>

            <p style={styles.helpText}>
              New user? Just enter your email and choose a password. 
              Your request will be sent to an administrator for approval.
            </p>
          </>
        )}

        {/* Footer */}
        <p style={styles.footer}>
          By signing in, you agree to our Terms of Service and Privacy Policy
        </p>
      </div>

      {/* Background decoration */}
      <div style={styles.bgGradient} />
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#0a1628',
    padding: 20,
    position: 'relative',
    overflow: 'hidden'
  },
  card: {
    background: 'linear-gradient(135deg, #0f2744 0%, #0a1628 100%)',
    border: '1px solid rgba(59, 130, 246, 0.2)',
    borderRadius: 16,
    padding: '48px 40px',
    maxWidth: 420,
    width: '100%',
    position: 'relative',
    zIndex: 1,
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
  },
  logoSection: {
    textAlign: 'center',
    marginBottom: 32
  },
  logoImage: {
    maxWidth: 280,
    height: 'auto'
  },
  title: {
    fontSize: 22,
    fontWeight: 600,
    color: '#ffffff',
    margin: '0 0 8px 0',
    textAlign: 'center'
  },
  subtitle: {
    fontSize: 14,
    color: '#94a3b8',
    margin: '0 0 32px 0',
    textAlign: 'center'
  },
  error: {
    background: 'rgba(239, 68, 68, 0.1)',
    border: '1px solid rgba(239, 68, 68, 0.3)',
    borderRadius: 8,
    padding: '12px 16px',
    marginBottom: 16,
    color: '#f87171',
    fontSize: 14
  },
  buttonContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12
  },
  oauthButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    padding: '14px 24px',
    background: 'rgba(255,255,255,0.1)',
    border: '1px solid rgba(255,255,255,0.2)',
    borderRadius: 8,
    color: '#ffffff',
    fontSize: 15,
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.2s ease'
  },
  icon: {
    width: 20,
    height: 20
  },
  divider: {
    display: 'flex',
    alignItems: 'center',
    margin: '24px 0'
  },
  dividerLine: {
    flex: 1,
    height: 1,
    background: 'rgba(255,255,255,0.1)'
  },
  dividerText: {
    padding: '0 16px',
    color: '#64748b',
    fontSize: 13
  },
  emailToggle: {
    width: '100%',
    padding: '14px 24px',
    background: 'transparent',
    border: '1px solid rgba(255,255,255,0.2)',
    borderRadius: 8,
    color: '#94a3b8',
    fontSize: 15,
    cursor: 'pointer',
    transition: 'all 0.2s ease'
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6
  },
  label: {
    fontSize: 13,
    color: '#94a3b8'
  },
  input: {
    padding: '12px 14px',
    background: 'rgba(0,0,0,0.3)',
    border: '1px solid rgba(59, 130, 246, 0.3)',
    borderRadius: 8,
    color: '#ffffff',
    fontSize: 15,
    outline: 'none'
  },
  submitButton: {
    padding: '14px 24px',
    background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
    border: 'none',
    borderRadius: 8,
    color: '#ffffff',
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
    marginTop: 8
  },
  backLink: {
    background: 'transparent',
    border: 'none',
    color: '#64748b',
    fontSize: 14,
    cursor: 'pointer',
    marginTop: 16,
    padding: 0
  },
  helpText: {
    fontSize: 13,
    color: '#64748b',
    textAlign: 'center',
    marginTop: 16,
    lineHeight: 1.5
  },
  footer: {
    fontSize: 12,
    color: '#475569',
    textAlign: 'center',
    marginTop: 32
  },
  loading: {
    textAlign: 'center',
    color: '#94a3b8',
    padding: 40
  },
  bgGradient: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 800,
    height: 800,
    background: 'radial-gradient(circle, rgba(59, 130, 246, 0.08) 0%, transparent 70%)',
    pointerEvents: 'none'
  },
  pendingSection: {
    textAlign: 'center',
    padding: '24px 0'
  },
  pendingIcon: {
    fontSize: 48,
    marginBottom: 16
  },
  pendingTitle: {
    fontSize: 20,
    fontWeight: 600,
    color: '#ffffff',
    margin: '0 0 12px 0'
  },
  pendingMessage: {
    fontSize: 14,
    color: '#94a3b8',
    lineHeight: 1.6
  },
  backButton: {
    width: '100%',
    padding: '12px 24px',
    background: 'transparent',
    border: '1px solid rgba(255,255,255,0.2)',
    borderRadius: 8,
    color: '#94a3b8',
    fontSize: 14,
    cursor: 'pointer',
    marginTop: 24
  }
};
